import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FIXED_BASE } from "@/lib/ticketTypes";

export default function AddTicket() {
  const nav = useNavigate();
  const [type, setType] = useState("REGULAR");
  const [drinks, setDrinks] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const customRef = useRef(null);

  useEffect(() => {
    setDrinks(FIXED_BASE[type] || 1);
  }, [type]);

  useEffect(() => {
    if (useCustom) setTimeout(() => customRef.current?.focus(), 50);
  }, [useCustom]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        ticket_type: type,
        drinks_allowed: Number(drinks),
        guest_name: name || null,
        email: email || null,
        phone: phone || null,
        notes: notes || null,
      };
      if (useCustom && custom.trim()) body.custom_barcode = custom.trim();
      const { data } = await api.post("/tickets", body);
      toast.success(`Created ${data.ticket_id}`);
      nav(`/tickets/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Single</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">ADD TICKET</h1>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Guest Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="add-guest-name" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Ticket Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white" data-testid="add-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="REGULAR">Regular</SelectItem>
                <SelectItem value="VIP">VIP</SelectItem>
                <SelectItem value="EMPLOYEE">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Phone (optional)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-slate-400">Number of Drinks</Label>
            <Input type="number" min="1" value={drinks} disabled={!!FIXED_BASE[type]} onChange={(e) => setDrinks(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" data-testid="add-drinks" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-slate-400">Internal Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#0B1120] border-slate-700 text-white" />
          </div>
        </div>

        <div className="border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold text-white">Use existing physical barcode</div>
              <div className="text-xs text-slate-500">Assign a pre-printed barcode instead of generating a new one.</div>
            </div>
            <Switch checked={useCustom} onCheckedChange={setUseCustom} data-testid="use-custom-toggle" />
          </div>
          {useCustom && (
            <div className="mt-3">
              <Label className="text-xs tracking-widest uppercase text-slate-400">Custom Barcode Value</Label>
              <Input
                ref={customRef}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="bg-[#0B1120] border-slate-700 text-white font-mono-fb"
                placeholder="Scan the physical barcode or type its value"
                data-testid="custom-barcode-input"
              />
            </div>
          )}
        </div>

        <Button
          onClick={submit} disabled={busy}
          className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-base rounded-xl"
          data-testid="add-submit-btn"
        >
          {busy ? "Creating…" : "CREATE TICKET"}
        </Button>
      </div>
    </div>
  );
}
